import { useState, useRef, useEffect } from "react";
import { askBrain, ChatTurn } from "../chat/brain";
import { executeAction, type ChatAction } from "../chat/actions";
import { loadChat, saveChat } from "../store/drafts";
import { MicButton } from "../chat/voice";
import { OrioraBadge } from "../OrioraBadge";

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    'Tell me what to post and where — I\'ll draft it, you say the word, I post.\n\ne.g. "Draft a casual post for Bluesky about our launch" · "draft from my sources" · "approve the first one" · "post it now" · "coach me".',
};

// Device-native TTS (free, on-device — the fleet voice standard). Only TEXT went to the server;
// reading the reply aloud costs nothing and nothing extra leaves the device.
function speak(text: string) {
  try {
    if (typeof speechSynthesis === "undefined") return;
    speechSynthesis.cancel();
    speechSynthesis.speak(new SpeechSynthesisUtterance(text));
  } catch { /* no TTS on this webview → button is harmless */ }
}

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  // Restore the conversation from local storage (device-only; lost on clear/reinstall).
  const [turns, setTurns] = useState<ChatTurn[]>(() => {
    const saved = loadChat() as ChatTurn[];
    return saved.length ? saved : [GREETING];
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // post_now actions are held here until the user taps Confirm — the brain proposing a post is NOT
  // enough to publish (a chat turn can carry injected text). Posting needs the user's explicit tap.
  // G17: the REST of the queue (actions after the held post) waits with it and runs on Confirm.
  const [pending, setPending] = useState<ChatAction[] | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]); // persist locally on every change

  // Run a queue of actions IN ORDER (G17). Stops and holds the remainder when it reaches a
  // post_now (destructive) — the messages so far are returned along with the held tail.
  async function runQueue(queue: ChatAction[]): Promise<{ messages: string[]; changed: boolean; held: ChatAction[] | null }> {
    const messages: string[] = [];
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const a = queue[i];
      if (a.kind === "post_now") {
        return { messages, changed, held: queue.slice(i) };
      }
      const result = await executeAction(a);
      if (result.message) messages.push(result.message);
      if (result.changed) changed = true;
    }
    return { messages, changed, held: null };
  }

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      const { reply, actions } = await askBrain(next);
      const { messages, changed, held } = await runQueue(actions);
      if (changed) onChanged?.();
      if (held) {
        setPending(held);
        const done = messages.length ? messages.join("\n\n") + "\n\n" : "";
        setTurns([...next, { role: "assistant", content: (reply ? `${reply}\n\n` : "") + done + "Ready to publish — tap Confirm to post, or Cancel to keep editing." }]);
        return;
      }
      const body = [reply, ...messages].filter(Boolean).join("\n\n");
      setTurns([...next, { role: "assistant", content: body || "Done." }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    try {
      // The held head is the post itself; anything after it resumes once the post has run.
      const [head, ...rest] = pending;
      const result = await executeAction(head);
      const msgs = result.message ? [result.message] : [];
      let changed = !!result.changed;
      if (result.ok && rest.length) {
        const tail = await runQueue(rest);
        msgs.push(...tail.messages);
        changed = changed || tail.changed;
        if (tail.held) { setPending(tail.held); setTurns((t) => [...t, { role: "assistant", content: msgs.join("\n\n") + "\n\nNext one is ready — tap Confirm to post it too." }]); if (changed) onChanged?.(); return; }
      }
      setTurns((t) => [...t, { role: "assistant", content: msgs.join("\n\n") || "Done." }]);
      if (changed) onChanged?.();
    } catch (e) {
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${String(e)}` }]);
    } finally {
      setPending(null);
      setBusy(false);
    }
  }

  function cancelPending() {
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — didn't post. Tell me what to change." }]);
  }

  return (
    <div className="chat">
      <div className="chat-scroll">
        {turns.map((t, i) => (
          <div key={i} className={`row ${t.role === "user" ? "me" : ""}`}>
            <div className={`bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role === "assistant" && i > 0 && (
                <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 10 }}>
                  <OrioraBadge />
                  <button
                    type="button"
                    onClick={() => speak(t.content)}
                    title="Read aloud"
                    aria-label="Read aloud"
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 13, color: "#71717a" }}
                  >🔊</button>
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="row"><div className="bubble bot thinking">…thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="composer confirm-bar">
          <button className="btn-primary" onClick={confirmPending} disabled={busy}>Confirm &amp; post</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <div className="composer">
          <textarea
            value={input}
            placeholder="Tell me what to post…"
            rows={2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
          />
          <MicButton value={input} onChange={setInput} disabled={busy} />
          <button className="btn-primary" onClick={send} disabled={busy}>Send</button>
        </div>
      )}
    </div>
  );
}
